import { Link } from 'react-router-dom';
import type { InsightArticle } from '@/mocks/insights';
import { insightArticles } from '@/mocks/insights';
import InsightCard from './InsightCard';

const categoryLabelMap: Record<string, string> = {
  strategy: '空間策略',
  design: '設計觀點',
  budget: '預算規劃',
  renovation: '翻新改造',
  brand: '品牌空間',
};

const categoryColorMap: Record<string, string> = {
  strategy: 'bg-amber-50 text-amber-700',
  design: 'bg-sky-50 text-sky-700',
  budget: 'bg-emerald-50 text-emerald-700',
  renovation: 'bg-rose-50 text-rose-700',
  brand: 'bg-violet-50 text-violet-700',
};

interface Props {
  article: InsightArticle;
}

export default function InsightDetailContent({ article }: Props) {
  const related = insightArticles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 2);

  const moreFallback = insightArticles
    .filter((a) => a.id !== article.id)
    .slice(0, 2 - related.length);

  const relatedArticles = [...related, ...moreFallback].slice(0, 2);

  const tagColor = categoryColorMap[article.category] ?? 'bg-gray-50 text-gray-600';
  const tagLabel = categoryLabelMap[article.category] ?? article.category;

  return (
    <article className="py-12 md:py-20 bg-[#fafaf8]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">

          {/* Main Article */}
          <div className="flex-1 min-w-0">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-[#9a9a9a] mb-8">
              <Link to="/insights" className="hover:text-[#b8956a] transition-colors cursor-pointer">專欄文章</Link>
              <i className="ri-arrow-right-s-line text-base"></i>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${tagColor}`}>{tagLabel}</span>
            </div>

            {/* Article header */}
            <div className="mb-8">
              <h1
                className="font-bold text-[#1a1a1a] leading-[1.3] mb-4"
                style={{ fontSize: 'clamp(1.5rem, 3vw, 2.25rem)' }}
              >
                {article.title}
              </h1>
              <p className="text-[#6a6a6a] leading-[1.75] mb-6" style={{ fontSize: '1.125rem' }}>
                {article.subtitle}
              </p>

              {/* Meta row */}
              <div className="flex flex-wrap items-center gap-4 pb-6 border-b border-[#ede9e4]">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0">
                    <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="text-[#1a1a1a] text-sm font-semibold">{article.author}</p>
                    <p className="text-[#9a9a9a] text-xs">{article.authorRole}</p>
                  </div>
                </div>
                <div className="w-px h-8 bg-[#ede9e4]"></div>
                <div className="flex items-center gap-1.5 text-[#9a9a9a] text-sm">
                  <i className="ri-calendar-line text-base"></i>
                  {article.publishDate.replace(/-/g, '.')}
                </div>
                <div className="flex items-center gap-1.5 text-[#9a9a9a] text-sm">
                  <i className="ri-time-line text-base"></i>
                  {article.readTime}閱讀
                </div>
              </div>
            </div>

            {/* Cover image */}
            <div className="w-full h-[280px] md:h-[420px] rounded-2xl overflow-hidden mb-10">
              <img
                src={article.coverImg}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Excerpt */}
            <div className="p-6 rounded-xl bg-[#f4f2ef] border-l-4 border-[#b8956a] mb-10">
              <p className="text-[#4a4a4a] leading-[1.85] italic" style={{ fontSize: '1.0625rem' }}>
                {article.excerpt}
              </p>
            </div>

            {/* Article sections */}
            <div className="flex flex-col gap-10">
              {article.sections.map((section, i) => (
                <div key={i}>
                  <h2
                    className="font-bold text-[#1a1a1a] mb-4 flex items-start gap-3"
                    style={{ fontSize: '1.25rem' }}
                  >
                    <span className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-[#b8956a] text-white text-xs font-bold mt-0.5">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    {section.heading}
                  </h2>
                  <div className="pl-10">
                    {section.body.split('\n').map((line, li) =>
                      line.trim() === '' ? (
                        <div key={li} className="h-3"></div>
                      ) : (
                        <p key={li} className="text-[#4a4a4a] leading-[1.9] text-[0.9375rem] mb-2">
                          {line}
                        </p>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Tags */}
            <div className="mt-10 pt-8 border-t border-[#ede9e4]">
              <p className="text-xs text-[#9a9a9a] uppercase tracking-wider mb-3">相關標籤</p>
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span key={tag} className="text-sm px-3 py-1.5 bg-white border border-[#e2ddd8] text-[#5a5a5a] rounded-full">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Back button */}
            <div className="mt-10">
              <Link
                to="/insights"
                className="inline-flex items-center gap-2 text-[#b8956a] font-medium hover:text-[#a07d58] transition-colors cursor-pointer text-sm"
              >
                <i className="ri-arrow-left-line"></i>
                返回所有文章
              </Link>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:w-72 flex-shrink-0">
            <div className="lg:sticky lg:top-28 flex flex-col gap-8">

              {/* Author card */}
              <div className="p-6 rounded-xl bg-white border border-[#ede9e4]">
                <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#b8956a] mb-4">文章作者</p>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                    <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1a1a1a]">{article.author}</p>
                    <p className="text-[#9a9a9a] text-xs">{article.authorRole}</p>
                  </div>
                </div>
                <p className="text-[#6a6a6a] text-sm leading-relaxed">
                  擁有 10+ 年商業空間策略與設計經驗，服務過餐飲、零售、醫美等多元產業。
                </p>
              </div>

              {/* Related articles */}
              {relatedArticles.length > 0 && (
                <div>
                  <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#b8956a] mb-5">相關文章</p>
                  <div className="flex flex-col gap-5">
                    {relatedArticles.map((a) => (
                      <InsightCard key={a.id} article={a} variant="horizontal" />
                    ))}
                  </div>
                </div>
              )}

              {/* CTA */}
              <div className="p-6 rounded-xl bg-[#1a1a1a]">
                <p className="text-[#b8956a] text-xs font-medium uppercase tracking-widest mb-3">免費諮詢</p>
                <p className="text-white font-semibold leading-snug mb-4" style={{ fontSize: '1rem' }}>
                  有這方面的需求？我們來談談
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#b8956a] text-white text-sm font-medium rounded-full hover:bg-[#a07d58] transition-colors cursor-pointer whitespace-nowrap"
                >
                  預約諮詢
                  <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>

            </div>
          </aside>
        </div>
      </div>
    </article>
  );
}
